import React, { useEffect, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Divider,
  Group,
  NumberInput,
  Select,
  Table,
  Text,
  TextInput,
  Textarea,
  Title,
  ActionIcon,
  Stack,
  Switch,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { IconPlus, IconTrash, IconPrinter } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { useNavigate } from "react-router-dom";
import HttpClient from "../helpers/HttpClient";
import { Invoice, InvoicePosition } from "../types/invoice.type";
import { Article } from "../types/article.type";
import { InvoiceSetting } from "../types/invoice-setting.type";
import { generateInvoicePdf } from "../helpers/invoicePdf";
import { useLanguage } from "../providers/LanguageContext";

const invoiceClient = new HttpClient<Invoice>("/invoice");
const articleClient = new HttpClient<Article>("/article");
const settingsClient = new HttpClient<InvoiceSetting>("/invoice-setting");

interface PositionRow {
  id?: number;
  articleId?: number;
  description: string;
  quantity: number;
  unitPrice: number;
  sortOrder: number;
}

function emptyPosition(sortOrder: number): PositionRow {
  return { description: "", quantity: 1, unitPrice: 0, sortOrder };
}

const InvoiceEdit: React.FC<{ id?: number }> = ({ id }) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [articles, setArticles] = useState<Article[]>([]);
  const [pdfSettings, setPdfSettings] = useState<InvoiceSetting | undefined>(undefined);

  const [invoiceDate, setInvoiceDate] = useState<Date>(new Date());
  const [recipientLine1, setRecipientLine1] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [recipientStreet, setRecipientStreet] = useState("");
  const [recipientCity, setRecipientCity] = useState("");
  const [postText, setPostText] = useState("Mit der Bitte um Überweisung auf unser Nachwuchskonto (NW IBAN siehe unten).");
  const [vatText, setVatText] = useState("steuerbefreit");
  const [vatRate, setVatRate] = useState<number>(0);
  const [positions, setPositions] = useState<PositionRow[]>([emptyPosition(0)]);
  const [isFixed, setIsFixed] = useState(false);
  const [isCancelled, setIsCancelled] = useState(false);
  const [isCancellationDocument, setIsCancellationDocument] = useState(false);
  const [relatedInvoiceId, setRelatedInvoiceId] = useState<number | undefined>(undefined);
  const [relatedInvoiceNumber, setRelatedInvoiceNumber] = useState<string | undefined>(undefined);
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [leistungszeitraum, setLeistungszeitraum] = useState("");
  const [isPaid, setIsPaid] = useState(false);
  const [paidDate, setPaidDate] = useState<Date | null>(null);
  const [paymentSaving, setPaymentSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [arts, s] = await Promise.all([
          articleClient.getAll(),
          settingsClient.getSingle().catch(() => undefined),
        ]);
        setArticles(arts);
        if (s) setPdfSettings(s);
        if (id) {
          const res = await invoiceClient.getById(Number(id));
          const inv = res.data as Invoice;
          setInvoiceDate(new Date(inv.invoiceDate));
          setRecipientLine1(inv.recipientLine1 ?? "");
          setRecipientName(inv.recipientName ?? "");
          setRecipientStreet(inv.recipientStreet ?? "");
          setRecipientCity(inv.recipientCity ?? "");
          setPostText(inv.postText ?? "");
          setVatText(inv.vatText ?? "steuerbefreit");
          setVatRate(Number(inv.vatRate) ?? 0);
          setIsFixed(inv.isFixed);
          setIsCancelled(inv.isCancelled);
          setIsCancellationDocument(inv.isCancellationDocument ?? false);
          setRelatedInvoiceId(inv.relatedInvoiceId);
          setRelatedInvoiceNumber(inv.relatedInvoiceNumber);
          setInvoiceNumber(inv.invoiceNumber ?? "");
          setLeistungszeitraum(inv.leistungszeitraum ?? "");
          setIsPaid(inv.isPaid ?? false);
          setPaidDate(inv.paidDate ? new Date(inv.paidDate) : null);
          setPositions(
            (inv.positions ?? [])
              .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
              .map((p: InvoicePosition) => ({
                id: p.id,
                articleId: p.article?.id,
                description: p.description,
                quantity: Number(p.quantity),
                unitPrice: Number(p.unitPrice),
                sortOrder: p.sortOrder ?? 0,
              }))
          );
        }
      } catch {
        notifications.show({ message: t.invoice.loadError, color: "red" });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const isReadonly = isFixed || isCancelled;

  const subtotal = positions.reduce((s, p) => s + p.quantity * p.unitPrice, 0);
  const vatAmount = subtotal * (vatRate / 100);
  const total = subtotal + vatAmount;

  const fmtEur = (v: number) =>
    `€ ${v.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const updatePosition = (idx: number, patch: Partial<PositionRow>) => {
    setPositions(prev => prev.map((p, i) => (i === idx ? { ...p, ...patch } : p)));
  };

  const addPosition = () => {
    setPositions(prev => [...prev, emptyPosition(prev.length)]);
  };

  const removePosition = (idx: number) => {
    setPositions(prev => prev.filter((_, i) => i !== idx));
  };

  const applyArticle = (idx: number, articleId: string | null) => {
    if (!articleId) return;
    const art = articles.find(a => String(a.id) === articleId);
    if (!art) return;
    updatePosition(idx, {
      articleId: art.id,
      description: art.description ?? art.name,
      unitPrice: Number(art.unitPrice ?? 0),
    });
  };

  const buildPayload = () => ({
    id: id ? Number(id) : undefined,
    invoiceDate,
    recipientLine1,
    recipientName,
    recipientStreet,
    recipientCity,
    postText,
    vatText,
    vatRate,
    leistungszeitraum: leistungszeitraum || undefined,
    positions: positions.map((p, i) => ({
      ...(p.id !== undefined ? { id: p.id } : {}),
      description: p.description,
      quantity: p.quantity,
      unitPrice: p.unitPrice,
      sortOrder: i,
      article: p.articleId ? ({ id: p.articleId } as Article) : undefined,
    })),
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = buildPayload();
      const res = await invoiceClient.post(payload as any);
      const data = res as any;
      if (data?.status !== 200 && data?.status !== 201) throw new Error();
      notifications.show({ message: id ? t.invoice.saveSuccess : t.invoice.createSuccess, color: "green" });
      navigate("/invoice");
    } catch {
      notifications.show({ message: t.invoice.saveFailed, color: "red" });
    } finally {
      setSaving(false);
    }
  };

  const handleTogglePaid = async () => {
    if (!id) return;
    setPaymentSaving(true);
    try {
      const client = new HttpClient<Invoice>("/invoice");
      if (isPaid) {
        await client.postAction(`${id}/mark-unpaid`, {});
        setIsPaid(false);
        setPaidDate(null);
      } else {
        const date = new Date();
        await client.postAction(`${id}/mark-paid`, { paidDate: date.toISOString() });
        setIsPaid(true);
        setPaidDate(date);
      }
      notifications.show({ message: "Zahlungsstatus aktualisiert", color: "green" });
    } catch {
      notifications.show({ message: "Fehler beim Aktualisieren des Zahlungsstatus", color: "red" });
    } finally {
      setPaymentSaving(false);
    }
  };

  const handleSavePaidDate = async (date: Date | null) => {
    if (!id || !date) return;
    setPaymentSaving(true);
    try {
      await new HttpClient<Invoice>("/invoice").postAction(`${id}/mark-paid`, { paidDate: date.toISOString() });
      setPaidDate(date);
      setIsPaid(true);
    } catch {
      notifications.show({ message: "Fehler beim Speichern des Zahlungsdatums", color: "red" });
    } finally {
      setPaymentSaving(false);
    }
  };

  const handlePrint = async () => {
    const inv: Invoice = {
      id: Number(id ?? 0),
      invoiceNumber: invoiceNumber || t.invoice.draft,
      invoiceDate,
      recipientLine1,
      recipientName,
      recipientStreet,
      recipientCity,
      postText,
      vatText,
      vatRate,
      isFixed,
      isCancelled,
      isCancellationDocument,
      relatedInvoiceId,
      leistungszeitraum: leistungszeitraum || undefined,
      positions: positions.map((p, i) => ({
        id: p.id ?? 0,
        description: p.description,
        quantity: p.quantity,
        unitPrice: p.unitPrice,
        sortOrder: i,
        createdAt: new Date(),
        updatedAt: new Date(),
      })),
      createdAt: new Date(),
      updatedAt: new Date(),
    };
    await generateInvoicePdf(inv, pdfSettings ?? undefined);
  };

  if (loading) return <div>{t.common.loading}</div>;

  const titleText = id
    ? invoiceNumber
      ? `${t.invoice.editTitle} ${invoiceNumber}`
      : `${t.invoice.editTitle} (${t.invoice.draft})`
    : t.invoice.newTitle;

  return (
    <Box mx="auto" maw={900} mt="lg" px="md">
      <Group justify="space-between" mb="md">
        <Title order={3}>
          {titleText}
          {isCancellationDocument && <Badge color="red" ml="xs">{t.invoice.cancellationBadge}</Badge>}
          {!isCancellationDocument && isFixed && <Text span c="green" ml="xs" fz="sm">({t.invoice.fixedBadge})</Text>}
          {!isCancellationDocument && isCancelled && <Text span c="red" ml="xs" fz="sm">({t.invoice.cancelledBadge})</Text>}
        </Title>
        <Group>
          <Button
            leftSection={<IconPrinter size={16} />}
            variant="light"
            onClick={handlePrint}
          >
            {t.invoice.pdfPrint}
          </Button>
          {!isReadonly && (
            <Button onClick={handleSave} loading={saving}>
              {t.common.save}
            </Button>
          )}
          <Button variant="subtle" onClick={() => navigate("/invoice")}>
            {t.common.back}
          </Button>
        </Group>
      </Group>

      {relatedInvoiceId && (
        <Text size="sm" c="dimmed" mb="sm">
          {t.invoice.relatedLabel}: {relatedInvoiceNumber ?? `#${relatedInvoiceId}`}
        </Text>
      )}

      <Stack gap="sm">
        <Group grow align="flex-start">
          <Stack gap="xs">
            <Text fw={600} size="sm">{t.invoice.recipientSection}</Text>
            <TextInput
              label={t.invoice.recipientOrg}
              placeholder="z.B. 1. SSK 1919"
              value={recipientLine1}
              onChange={e => setRecipientLine1(e.currentTarget.value)}
              disabled={isReadonly}
            />
            <TextInput
              label={t.invoice.recipientName}
              placeholder="z.B. Franz Holzner"
              value={recipientName}
              onChange={e => setRecipientName(e.currentTarget.value)}
              disabled={isReadonly}
            />
            <TextInput
              label={t.invoice.recipientStreet}
              placeholder="z.B. Parscher Straße 4"
              value={recipientStreet}
              onChange={e => setRecipientStreet(e.currentTarget.value)}
              disabled={isReadonly}
            />
            <TextInput
              label={t.invoice.recipientCity}
              placeholder="z.B. 5023 Salzburg"
              value={recipientCity}
              onChange={e => setRecipientCity(e.currentTarget.value)}
              disabled={isReadonly}
            />
          </Stack>
          <Stack gap="xs" maw={200}>
            <DateInput
              label={t.invoice.dateLabel}
              value={invoiceDate}
              onChange={(v: Date | string | null) => setInvoiceDate(v ? new Date(v) : new Date())}
              valueFormat="DD.MM.YYYY"
              disabled={isReadonly}
            />
            <TextInput
              label="Leistungszeitraum"
              placeholder="z.B. Jänner 2025"
              value={leistungszeitraum}
              onChange={e => setLeistungszeitraum(e.currentTarget.value)}
              disabled={isReadonly}
            />
            {isFixed && !isCancelled && (
              <>
                <Switch
                  label={t.invoice.paidBadge}
                  checked={isPaid}
                  onChange={handleTogglePaid}
                  color="teal"
                  disabled={paymentSaving}
                  mt="xs"
                />
                {isPaid && (
                  <DateInput
                    label={t.invoice.paidDateLabel}
                    value={paidDate}
                    onChange={(v: Date | string | null) => handleSavePaidDate(v ? new Date(v) : new Date())}
                    valueFormat="DD.MM.YYYY"
                    disabled={paymentSaving}
                  />
                )}
              </>
            )}
          </Stack>
        </Group>

        <Divider my="sm" label={t.invoice.positionsSection} labelPosition="left" />

        <Table withTableBorder withColumnBorders>
          <Table.Thead>
            <Table.Tr>
              <Table.Th w={160}>{t.invoice.articleCol}</Table.Th>
              <Table.Th>{t.invoice.descriptionCol}</Table.Th>
              <Table.Th w={90}>{t.invoice.quantityCol}</Table.Th>
              <Table.Th w={130}>{t.invoice.unitPriceCol}</Table.Th>
              <Table.Th w={110}>{t.invoice.sumCol}</Table.Th>
              {!isReadonly && <Table.Th w={40} />}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {positions.map((pos, idx) => (
              <Table.Tr key={idx}>
                <Table.Td>
                  <Select
                    data={articles.map(a => ({ value: String(a.id), label: a.name }))}
                    placeholder="Artikel wählen"
                    value={pos.articleId ? String(pos.articleId) : null}
                    onChange={v => applyArticle(idx, v)}
                    clearable
                    disabled={isReadonly}
                    size="xs"
                  />
                </Table.Td>
                <Table.Td>
                  <TextInput
                    value={pos.description}
                    onChange={e => updatePosition(idx, { description: e.currentTarget.value })}
                    disabled={isReadonly}
                    size="xs"
                  />
                </Table.Td>
                <Table.Td>
                  <NumberInput
                    value={pos.quantity}
                    onChange={v => updatePosition(idx, { quantity: Number(v) || 0 })}
                    disabled={isReadonly}
                    min={0}
                    allowDecimal
                    decimalSeparator=","
                    size="xs"
                  />
                </Table.Td>
                <Table.Td>
                  <NumberInput
                    value={pos.unitPrice}
                    onChange={v => updatePosition(idx, { unitPrice: Number(v) || 0 })}
                    disabled={isReadonly}
                    allowDecimal
                    decimalSeparator=","
                    decimalScale={2}
                    fixedDecimalScale
                    prefix="€ "
                    size="xs"
                  />
                </Table.Td>
                <Table.Td ta="right">
                  <Text size="sm">{fmtEur(pos.quantity * pos.unitPrice)}</Text>
                </Table.Td>
                {!isReadonly && (
                  <Table.Td>
                    <ActionIcon
                      color="red"
                      variant="light"
                      size="sm"
                      onClick={() => removePosition(idx)}
                    >
                      <IconTrash size={14} />
                    </ActionIcon>
                  </Table.Td>
                )}
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>

        {!isReadonly && (
          <Button
            leftSection={<IconPlus size={14} />}
            variant="light"
            size="xs"
            onClick={addPosition}
            w="fit-content"
          >
            {t.invoice.addPosition}
          </Button>
        )}

        <Group justify="flex-end">
          <Table w={320} withTableBorder>
            <Table.Tbody>
              <Table.Tr>
                <Table.Td>{t.invoice.subtotal}</Table.Td>
                <Table.Td ta="right">{fmtEur(subtotal)}</Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Td>
                  <Group gap="xs" wrap="nowrap">
                    <Text size="sm">{t.invoice.vat}</Text>
                    <NumberInput
                      value={vatRate}
                      onChange={v => setVatRate(Number(v) || 0)}
                      disabled={isReadonly}
                      min={0}
                      max={100}
                      suffix="%"
                      size="xs"
                      w={80}
                    />
                  </Group>
                </Table.Td>
                <Table.Td ta="right">{fmtEur(vatAmount)}</Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Td fw={700}>{t.invoice.totalAmount}</Table.Td>
                <Table.Td ta="right" fw={700}>{fmtEur(total)}</Table.Td>
              </Table.Tr>
            </Table.Tbody>
          </Table>
        </Group>

        <Divider my="sm" label={t.invoice.textsSection} labelPosition="left" />

        <Textarea
          label={t.invoice.postTextLabel}
          placeholder="z.B. Mit der Bitte um Überweisung auf unser Nachwuchskonto..."
          rows={3}
          value={postText}
          onChange={e => setPostText(e.currentTarget.value)}
          disabled={isReadonly}
        />

        <TextInput
          label={t.invoice.vatTextLabel}
          placeholder="z.B. steuerbefreit"
          value={vatText}
          onChange={e => setVatText(e.currentTarget.value)}
          disabled={isReadonly}
          maw={300}
        />
      </Stack>
    </Box>
  );
};

export default InvoiceEdit;
